'use client';

import Logo from '@public/logos/logo-cesky-volejbal.svg';
import { ArrowLeft } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next-nprogress-bar';
import { useMemo } from 'react';

import { StatusIcon } from '@/components/icons/status-icon';
import type { SidebarRoute } from '@/components/navbar/navigation.config';
import { Button } from '@/components/ui/button';
import useFilteredNavigation from '@/hooks/use-filtered-navigation';
import type { ModuleTag } from '@/types';

interface NotFoundErrorProps<T extends ModuleTag> {
  sidebarRoutes?: SidebarRoute<T>[];
  errorTitle?: string;
}

export default function NotFoundError<T extends ModuleTag>({
  sidebarRoutes,
  errorTitle = 'Stránka nebyla nalezena',
}: NotFoundErrorProps<T>) {
  const router = useRouter();
  const pathname = usePathname();
  const linksWithAccess = useFilteredNavigation();

  const { targetRoute, targetLabel } = useMemo(() => {
    if (sidebarRoutes) {
      const sortedRoutes = [...sidebarRoutes].sort((a, b) => b.link.length - a.link.length);
      const foundRoute = sortedRoutes.find(route => pathname.startsWith(route.link));
      return {
        targetRoute: foundRoute?.link ?? '/',
        targetLabel: foundRoute?.title ?? 'domovskou stránku',
      };
    }
    const sortedLinks = [...linksWithAccess].sort((a, b) => b.href.length - a.href.length);
    const foundLink = sortedLinks.find(link => pathname.startsWith(link.href));
    return {
      targetRoute: foundLink?.href ?? '/',
      targetLabel: foundLink?.label ?? 'domovskou stránku',
    };
  }, [pathname, sidebarRoutes, linksWithAccess]);

  return (
    <div className="container flex min-h-screen w-full flex-col items-center justify-center gap-8">

      <div className="flex flex-col items-center">
        <StatusIcon success={false} className="h-12 w-auto" />
        <p className="text-gray-600">{errorTitle}</p>
      </div>
      <Button onClick={() => router.replace(targetRoute)} className="group flex w-auto items-center gap-1.5 px-4">
        <ArrowLeft className="transition-transform group-hover:-translate-x-1" />
        <div>
          Zpět na
          {' '}
          <span className="lowercase">{targetLabel}</span>
        </div>
      </Button>
      <Logo />
    </div>
  );
}
